import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  FlaskConical, Loader2, CheckCircle2, XCircle, Clock, Plus, Trash2,
  Copy, Check, Play, FolderOpen, AlertTriangle, ChevronRight, ChevronDown, Search,
  ArrowUpRight, Upload, UserRound, Scissors, RotateCcw,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AI_MODELS } from '../../../../convex/aiModels'

const DEFAULT_MODEL = 'veo-reference-to-video'

type Tab = 'history' | 'projects' | 'errors'

interface LoadParams {
  modelId?: string
  prompt?: string
  duration?: number
  aspectRatio?: '16:9' | '9:16'
  hasAudio?: boolean
  imageUrl?: string
  endFrameUrl?: string
  referenceImageUrls?: string[]
  sourceVideoUrl?: string
  negativePrompt?: string
  cfgScale?: number
  iterationGroupId?: string
  iterationIndex?: number
}

export default function AdminSandboxPage({ embedded = false }: { embedded?: boolean }) {
  const models = useQuery(api.sandbox.getModels)
  const videos = useQuery(api.sandbox.list)
  const generate = useMutation(api.sandbox.generate)

  const [modelId, setModelId] = useState('')
  const [prompt, setPrompt] = useState('')
  const [duration, setDuration] = useState(8)
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16'>('16:9')
  const [hasAudio, setHasAudio] = useState(true)
  const [imageUrl, setImageUrl] = useState('')
  const [endFrameUrl, setEndFrameUrl] = useState('')
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([''])
  const [sourceVideoUrl, setSourceVideoUrl] = useState('')
  const [keepAudio, setKeepAudio] = useState(false)
  const [negativePrompt, setNegativePrompt] = useState('')
  const [cfgScale, setCfgScale] = useState<number | ''>('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('history')
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null)
  const [formCollapsed, setFormCollapsed] = useState(false)
  const [iterationGroupId, setIterationGroupId] = useState<string | null>(null)
  const [iterationIndex, setIterationIndex] = useState(0)

  const selectedModel = useMemo(
    () => models?.find((m) => m.id === modelId),
    [models, modelId]
  )

  const durations = useMemo(() => {
    if (!selectedModel) return []
    if (selectedModel.validDurations) return selectedModel.validDurations
    if (selectedModel.durationRange) {
      const { min, max, step } = selectedModel.durationRange
      const result = []
      for (let d = min; d <= max; d += step) result.push(d)
      return result
    }
    return [5]
  }, [selectedModel])

  useMemo(() => {
    if (models && models.length > 0 && !modelId) {
      const defaultModel = models.find((m) => m.id === DEFAULT_MODEL) || models[0]
      setModelId(defaultModel.id)
    }
  }, [models, modelId])

  useMemo(() => {
    if (durations.length > 0 && !durations.includes(duration)) {
      setDuration(durations[0])
    }
  }, [durations, duration])

  const loadGeneration = useCallback((params: LoadParams) => {
    if (params.modelId && models?.find((m) => m.id === params.modelId)) {
      setModelId(params.modelId)
    }
    if (params.prompt) setPrompt(params.prompt)
    if (params.duration) setDuration(params.duration)
    if (params.aspectRatio) setAspectRatio(params.aspectRatio)
    if (params.hasAudio !== undefined) setHasAudio(params.hasAudio)
    if (params.imageUrl) setImageUrl(params.imageUrl)
    if (params.endFrameUrl) setEndFrameUrl(params.endFrameUrl)
    if (params.referenceImageUrls?.length) setReferenceImageUrls(params.referenceImageUrls)
    if (params.sourceVideoUrl) setSourceVideoUrl(params.sourceVideoUrl)
    if (params.negativePrompt) {
      setNegativePrompt(params.negativePrompt)
      setShowAdvanced(true)
    }
    if (params.cfgScale !== undefined) {
      setCfgScale(params.cfgScale)
      setShowAdvanced(true)
    }
    if (params.iterationGroupId) {
      setIterationGroupId(params.iterationGroupId)
      setIterationIndex((params.iterationIndex ?? 0) + 1)
    }
    setLoadedFrom(`Loaded: ${params.modelId || 'unknown'} — ${(params.prompt || '').slice(0, 60)}...`)
    setFormCollapsed(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [models])

  const { curlCommand, requestBody } = useMemo(() => {
    const modelConfig = AI_MODELS[modelId]
    if (!modelConfig || !selectedModel) return { curlCommand: '', requestBody: {} }

    const body: Record<string, unknown> = {}

    if (prompt.trim()) {
      body.prompt = prompt.trim()
    }

    if (modelConfig.durationFormat === 'string_s') {
      body.duration = `${duration}s`
    } else if (modelConfig.durationFormat === 'string') {
      body.duration = String(duration)
    } else if (modelConfig.durationFormat === 'integer') {
      body.duration = duration
    }

    body.aspect_ratio = aspectRatio

    if (modelConfig.supportsGenerateAudio && hasAudio !== undefined) {
      body.generate_audio = hasAudio
    } else if (modelConfig.audioAlwaysOn) {
      body.generate_audio = true
    }

    if (selectedModel.supportsReferenceImages) {
      const filteredRefs = referenceImageUrls.filter((u) => u.trim())
      if (filteredRefs.length > 0 && modelConfig.imageParams.refs) {
        body[modelConfig.imageParams.refs] = filteredRefs
      }
    } else if (selectedModel.supportsImage) {
      if (imageUrl.trim()) {
        body[modelConfig.imageParams.first] = imageUrl.trim()
      }
      if (selectedModel.supportsEndFrame && endFrameUrl.trim() && modelConfig.imageParams.last) {
        body[modelConfig.imageParams.last] = endFrameUrl.trim()
      }
    }

    if (selectedModel.supportsSourceVideo && sourceVideoUrl.trim() && modelConfig.videoParams) {
      body[modelConfig.videoParams.input] = sourceVideoUrl.trim()
    }

    if (selectedModel.supportsKeepAudio && keepAudio && modelConfig.videoParams?.keepAudio) {
      body[modelConfig.videoParams.keepAudio] = keepAudio
    }

    if (selectedModel.supportsNegativePrompt && negativePrompt.trim()) {
      body.negative_prompt = negativePrompt.trim()
    }

    if (selectedModel.supportsCfgScale && cfgScale !== '') {
      body.cfg_scale = cfgScale
    }

    const jsonBody = JSON.stringify(body, null, 2)

    const curl = `curl -X POST "https://queue.fal.run/${modelConfig.endpoint}" \\
  -H "Authorization: Key $FAL_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${jsonBody}'`

    return { curlCommand: curl, requestBody: body }
  }, [modelId, selectedModel, prompt, duration, aspectRatio, hasAudio, imageUrl, endFrameUrl, referenceImageUrls, sourceVideoUrl, keepAudio, negativePrompt, cfgScale])

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(curlCommand)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [curlCommand])

  async function handleGenerate() {
    if (!prompt.trim() || !modelId) return
    setError('')
    setSubmitting(true)
    try {
      const groupId = iterationGroupId || crypto.randomUUID()
      const idx = iterationGroupId ? iterationIndex : 0

      const filteredRefs = referenceImageUrls.filter((u) => u.trim())
      await generate({
        modelId,
        prompt: prompt.trim(),
        duration,
        aspectRatio,
        hasAudio: selectedModel?.supportsAudio ? hasAudio : undefined,
        imageUrl: selectedModel?.supportsImage && imageUrl ? imageUrl : undefined,
        endFrameUrl: selectedModel?.supportsEndFrame && endFrameUrl ? endFrameUrl : undefined,
        referenceImageUrls: selectedModel?.supportsReferenceImages && filteredRefs.length > 0 ? filteredRefs : undefined,
        sourceVideoUrl: selectedModel?.supportsSourceVideo && sourceVideoUrl ? sourceVideoUrl : undefined,
        keepAudio: selectedModel?.supportsKeepAudio ? keepAudio : undefined,
        negativePrompt: selectedModel?.supportsNegativePrompt && negativePrompt ? negativePrompt : undefined,
        cfgScale: selectedModel?.supportsCfgScale && cfgScale !== '' ? cfgScale : undefined,
        iterationGroupId: groupId,
        iterationIndex: idx,
      })
      setIterationGroupId(groupId)
      setIterationIndex(idx + 1)
      setLoadedFrom(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed')
    } finally {
      setSubmitting(false)
    }
  }

  const allModels = models ?? []

  return (
    <div className={cn(embedded ? 'space-y-6' : 'p-6 space-y-6')}>
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
            <FlaskConical className="h-6 w-6" />
            Sandbox
          </h1>
          <p className="text-sm text-neutral-500">
            Generate standalone videos for testing. No credits charged.
          </p>
        </div>
      )}

      {loadedFrom && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Upload className="h-4 w-4 shrink-0" />
          <span className="truncate">{loadedFrom}</span>
          <button
            onClick={() => setLoadedFrom(null)}
            className="ml-auto text-blue-400 hover:text-blue-600"
          >
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Top row: Form + Curl Builder (collapsible) */}
      <button
        onClick={() => setFormCollapsed(!formCollapsed)}
        className="flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors"
      >
        {formCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Generate Video
        {formCollapsed && prompt && (
          <span className="text-xs text-neutral-400 font-normal truncate max-w-md">
            — {prompt.slice(0, 80)}{prompt.length > 80 ? '...' : ''}
          </span>
        )}
      </button>
      {!formCollapsed && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left — Generation Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Generate Video</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Model</label>
              <select
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm bg-white"
              >
                {allModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id} ({m.category})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Prompt</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={8}
                placeholder="Describe the video you want to generate..."
                className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm resize-y"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Duration</label>
              <div className="flex gap-2 flex-wrap">
                {durations.map((d) => (
                  <button
                    key={d}
                    onClick={() => setDuration(d)}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      d === duration
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400'
                    )}
                  >
                    {d}s
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Aspect Ratio</label>
              <div className="flex gap-2">
                {(['16:9', '9:16'] as const).map((ar) => (
                  <button
                    key={ar}
                    onClick={() => setAspectRatio(ar)}
                    className={cn(
                      'px-3 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      ar === aspectRatio
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-400'
                    )}
                  >
                    {ar}
                  </button>
                ))}
              </div>
            </div>

            {selectedModel?.supportsAudio && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasAudio"
                  checked={hasAudio}
                  onChange={(e) => setHasAudio(e.target.checked)}
                  className="rounded border-neutral-300"
                />
                <label htmlFor="hasAudio" className="text-sm text-neutral-700">Generate audio</label>
              </div>
            )}

            {selectedModel?.supportsImage && !selectedModel?.supportsReferenceImages && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Image URL</label>
                <div className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  />
                  {imageUrl && <img src={imageUrl} className="w-16 h-10 object-cover rounded border border-neutral-200 shrink-0" />}
                </div>
              </div>
            )}

            {selectedModel?.supportsEndFrame && !selectedModel?.supportsReferenceImages && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End Frame URL</label>
                <div className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={endFrameUrl}
                    onChange={(e) => setEndFrameUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  />
                  {endFrameUrl && <img src={endFrameUrl} className="w-16 h-10 object-cover rounded border border-neutral-200 shrink-0" />}
                </div>
              </div>
            )}

            {selectedModel?.supportsReferenceImages && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Image URLs
                </label>
                <div className="space-y-2">
                  {referenceImageUrls.map((url, i) => (
                    <div key={i} className="flex gap-2 items-start">
                      <input
                        type="text"
                        value={url}
                        onChange={(e) => {
                          const next = [...referenceImageUrls]
                          next[i] = e.target.value
                          setReferenceImageUrls(next)
                        }}
                        placeholder="https://..."
                        className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
                      />
                      {url && <img src={url} className="w-16 h-10 object-cover rounded border border-neutral-200 shrink-0" />}
                      {referenceImageUrls.length > 1 && (
                        <button
                          onClick={() => setReferenceImageUrls(referenceImageUrls.filter((_, j) => j !== i))}
                          className="text-neutral-400 hover:text-red-500 p-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setReferenceImageUrls([...referenceImageUrls, ''])}
                    className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add image
                  </button>
                </div>
              </div>
            )}

            {selectedModel?.supportsSourceVideo && (
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Source Video URL</label>
                <div className="flex gap-2 items-start">
                  <input
                    type="text"
                    value={sourceVideoUrl}
                    onChange={(e) => setSourceVideoUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
                  />
                  {sourceVideoUrl && <video src={sourceVideoUrl} className="w-20 h-12 object-cover rounded border border-neutral-200 shrink-0" preload="none" />}
                </div>
              </div>
            )}

            {selectedModel?.supportsKeepAudio && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="keepAudio"
                  checked={keepAudio}
                  onChange={(e) => setKeepAudio(e.target.checked)}
                  className="rounded border-neutral-300"
                />
                <label htmlFor="keepAudio" className="text-sm text-neutral-700">Keep original audio</label>
              </div>
            )}

            {(selectedModel?.supportsNegativePrompt || selectedModel?.supportsCfgScale) && (
              <div>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-sm text-neutral-500 hover:text-neutral-700"
                >
                  {showAdvanced ? '- Hide' : '+ Show'} advanced options
                </button>
                {showAdvanced && (
                  <div className="mt-2 space-y-3">
                    {selectedModel?.supportsNegativePrompt && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          Negative Prompt
                        </label>
                        <textarea
                          value={negativePrompt}
                          onChange={(e) => setNegativePrompt(e.target.value)}
                          rows={2}
                          placeholder="Things to avoid..."
                          className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm resize-none"
                        />
                      </div>
                    )}
                    {selectedModel?.supportsCfgScale && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">
                          CFG Scale
                        </label>
                        <input
                          type="number"
                          value={cfgScale}
                          onChange={(e) => setCfgScale(e.target.value ? Number(e.target.value) : '')}
                          placeholder="0.5"
                          step="0.1"
                          min="0"
                          max="1"
                          className="w-32 rounded-md border border-neutral-300 px-3 py-2 text-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right — CURL Request Builder */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Request</CardTitle>
            <button
              onClick={handleCopy}
              className="text-neutral-400 hover:text-neutral-600 p-1 rounded transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col gap-4">
            <div className="flex-1 bg-neutral-950 rounded-lg p-4 overflow-auto">
              <pre className="text-[13px] leading-relaxed font-mono text-neutral-300 whitespace-pre-wrap break-all">
                <span className="text-green-400">curl</span>{' '}
                <span className="text-yellow-300">-X POST</span>{' '}
                <span className="text-cyan-300">{`"https://queue.fal.run/${selectedModel?.endpoint || '...'}"`}</span>{' '}
                <span className="text-neutral-500">\</span>
                {'\n  '}
                <span className="text-yellow-300">-H</span>{' '}
                <span className="text-cyan-300">{`"Authorization: Key $FAL_KEY"`}</span>{' '}
                <span className="text-neutral-500">\</span>
                {'\n  '}
                <span className="text-yellow-300">-H</span>{' '}
                <span className="text-cyan-300">{`"Content-Type: application/json"`}</span>{' '}
                <span className="text-neutral-500">\</span>
                {'\n  '}
                <span className="text-yellow-300">-d</span>{' '}
                <span className="text-neutral-500">{'\''}
                </span>
                <JsonHighlight value={requestBody} />
                <span className="text-neutral-500">{'\''}
                </span>
              </pre>
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <Button
              onClick={handleGenerate}
              disabled={submitting || !prompt.trim() || !modelId}
              className="w-full"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Request
            </Button>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Iteration Timeline */}
      {iterationGroupId && (
        <IterationTimeline
          iterationGroupId={iterationGroupId}
          onNewSession={() => {
            setIterationGroupId(null)
            setIterationIndex(0)
          }}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-neutral-200">
        <div className="flex gap-0">
          {([
            { id: 'history' as Tab, label: 'History', icon: Clock },
            { id: 'projects' as Tab, label: 'Videos', icon: FolderOpen },
            { id: 'errors' as Tab, label: 'Errors', icon: AlertTriangle },
          ]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors',
                activeTab === id
                  ? 'border-neutral-900 text-neutral-900'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'history' && (
        <HistoryTab videos={videos} onLoad={loadGeneration} />
      )}
      {activeTab === 'projects' && (
        <ProjectsTab onLoad={loadGeneration} />
      )}
      {activeTab === 'errors' && (
        <ErrorsTab onLoad={loadGeneration} />
      )}
    </div>
  )
}

// ─── History Tab ───

function HistoryTab({
  videos,
  onLoad,
}: {
  videos: any[] | undefined
  onLoad: (params: LoadParams) => void
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Sandbox History</CardTitle>
      </CardHeader>
      <CardContent>
        {videos === undefined ? (
          <div className="flex items-center justify-center py-8 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8">No sandbox videos yet</p>
        ) : (
          <div className="divide-y divide-neutral-100">
            {videos.map((video) => (
              <div
                key={video._id}
                className="flex items-start gap-4 py-3 first:pt-0 last:pb-0"
              >
                {video.status === 'succeeded' && video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    className="w-48 shrink-0 h-[108px] object-cover rounded-md bg-neutral-100"
                  />
                ) : (
                  <div className="w-48 shrink-0 h-[108px] rounded-md bg-neutral-100 flex items-center justify-center">
                    <StatusIcon status={video.status} />
                  </div>
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={video.status} />
                    <span className="text-xs font-mono bg-neutral-100 px-1.5 py-0.5 rounded truncate">
                      {video.modelId}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {video.duration}s · {video.aspectRatio}
                    </span>
                    <span className="text-xs text-neutral-400 ml-auto">
                      {formatTimeAgo(video.createdAt)}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-700 line-clamp-2">
                    {video.prompt || '(no prompt)'}
                  </p>
                  {video.status === 'failed' && video.errorMessage && (
                    <p className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">
                      {video.errorMessage}
                    </p>
                  )}
                  <button
                    onClick={() =>
                      onLoad({
                        modelId: video.modelId,
                        prompt: video.prompt,
                        duration: video.duration,
                        aspectRatio: video.aspectRatio,
                        hasAudio: video.hasAudio,
                        imageUrl: video.metadata?.images?.[0],
                        endFrameUrl: video.metadata?.images?.[1],
                        referenceImageUrls: video.metadata?.referenceImages,
                        sourceVideoUrl: video.metadata?.sourceVideoUrl,
                        negativePrompt: video.metadata?.negativePrompt,
                        cfgScale: video.metadata?.cfgScale,
                        iterationGroupId: video.metadata?.iterationGroupId,
                        iterationIndex: video.metadata?.iterationIndex,
                      })
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    Load
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Projects Tab ───

function ProjectsTab({ onLoad }: { onLoad: (params: LoadParams) => void }) {
  const [search, setSearch] = useState('')
  const [expandedProject, setExpandedProject] = useState<Id<'workflows'> | null>(null)

  const result = useQuery(api.sandbox.browseProjects, {
    search: search || undefined,
    limit: 20,
  })

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
        <input
          type="text"
          placeholder="Search videos..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {result === undefined ? (
            <div className="flex items-center justify-center py-8 text-neutral-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : result.projects.length === 0 ? (
            <p className="text-sm text-neutral-400 text-center py-8">No videos found</p>
          ) : (
            <div className="divide-y divide-neutral-100">
              {result.projects.map((project) => (
                <div key={project._id}>
                  <button
                    onClick={() =>
                      setExpandedProject(
                        expandedProject === project._id ? null : project._id
                      )
                    }
                    className="w-full flex items-center gap-3 p-3 hover:bg-neutral-50 transition-colors text-left"
                  >
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 text-neutral-400 transition-transform shrink-0',
                        expandedProject === project._id && 'rotate-90'
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-neutral-900 truncate">
                          {project.title}
                        </p>
                        {project.mode && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 shrink-0">
                            {project.mode}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-neutral-400">
                          {formatTimeAgo(project.updatedAt)}
                        </span>
                        {project.characterNames?.length > 0 && (
                          <>
                            <span className="text-neutral-300">·</span>
                            <span className="text-xs text-neutral-500 truncate">
                              {project.characterNames.join(', ')}
                            </span>
                          </>
                        )}
                      </div>
                      {project.narrativeSnippet && (
                        <p className="text-xs text-neutral-400 truncate mt-0.5">
                          {project.narrativeSnippet}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          project.status === 'completed'
                            ? 'bg-green-50 text-green-700'
                            : project.status === 'generating'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-neutral-100 text-neutral-600'
                        )}
                      >
                        {project.status}
                      </span>
                      <a
                        href={`/video/${project.shortcode ?? project._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-neutral-400 hover:text-neutral-600"
                        title="Open video"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </a>
                    </div>
                  </button>

                  {expandedProject === project._id && (
                    <ProjectClips
                      workflowId={project._id}
                      onLoad={onLoad}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ProjectClips({
  workflowId,
  onLoad,
}: {
  workflowId: Id<'workflows'>
  onLoad: (params: LoadParams) => void
}) {
  const data = useQuery(api.sandbox.getProjectClips, { workflowId })

  if (data === undefined) {
    return (
      <div className="flex items-center justify-center py-6 text-neutral-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }

  if (!data || data.clips.length === 0) {
    return (
      <p className="text-sm text-neutral-400 text-center py-4">No clips</p>
    )
  }

  const charRefs = data.workflow.characterRefs as Record<string, { name: string; images: string[] }> | undefined

  return (
    <div className="bg-neutral-50 border-t border-neutral-100 p-3 space-y-3">
      {data.clips.map((clip) => {
        const meta = clip.video?.metadata as Record<string, any> | undefined
        const clipChars = (clip.charactersInClip || []) as string[]
        const keyframes = (clip.keyframes || []) as Array<{ url: string; timestamp: number; description: string; status?: string }>

        return (
          <div
            key={clip._id}
            className="bg-white border border-neutral-200 rounded-lg p-3 space-y-2"
          >
            {/* Header row */}
            <div className="flex items-center gap-2">
              <ClipStatusIcon status={clip.videoStatus} />
              <span className="text-xs font-medium text-neutral-700">
                {clip.sceneLabel ? `${clip.sceneLabel} · ` : ''}
                Clip {clip.orderIndex + 1}
                {clip.isJumpCut && (
                  <Scissors className="inline h-3 w-3 ml-1 text-amber-500" />
                )}
              </span>
              {clip.generationMode && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                  {clip.generationMode}
                </span>
              )}
              {clip.video?.modelId && (
                <span className="text-xs font-mono bg-neutral-100 px-1.5 py-0.5 rounded truncate ml-auto">
                  {clip.video.modelId}
                </span>
              )}
              <span className="text-xs text-neutral-400">{clip.duration}s</span>
              {clip.videoVersionCount > 1 && (
                <span className="text-xs text-neutral-400">v{clip.videoVersionCount}</span>
              )}
            </div>

            {clip.title && (
              <p className="text-xs font-medium text-neutral-600">{clip.title}</p>
            )}

            {/* Characters in clip */}
            {clipChars.length > 0 && charRefs && (
              <div className="flex items-center gap-2 flex-wrap">
                <UserRound className="h-3 w-3 text-neutral-400 shrink-0" />
                {clipChars.map((chId) => {
                  const ch = charRefs[chId]
                  return ch ? (
                    <div key={chId} className="flex items-center gap-1">
                      {ch.images[0] && (
                        <img src={ch.images[0]} className="w-5 h-5 rounded-full object-cover" />
                      )}
                      <span className="text-xs text-neutral-600">{ch.name}</span>
                    </div>
                  ) : null
                })}
              </div>
            )}

            {/* Media row: storyboard, keyframes, first/last frame, video */}
            <div className="flex gap-2 overflow-x-auto">
              {clip.storyboardImageUrl && (
                <MediaThumb url={clip.storyboardImageUrl} label="Sketch" />
              )}
              {keyframes.map((kf, i) => (
                <MediaThumb
                  key={i}
                  url={kf.url}
                  label={`KF ${kf.timestamp === 0 ? 'start' : kf.timestamp === 1 ? 'end' : `${kf.timestamp}`}`}
                  failed={kf.status === 'failed'}
                />
              ))}
              {clip.firstFrameUrl && (
                <MediaThumb url={clip.firstFrameUrl} label="1st frame" />
              )}
              {clip.lastFrameUrl && (
                <MediaThumb url={clip.lastFrameUrl} label="Last frame" />
              )}
            </div>

            {/* Video player */}
            {clip.videoUrl && (
              <video
                src={clip.videoUrl}
                controls
                className="max-w-xs rounded-md"
                preload="none"
              />
            )}

            {/* Prompt */}
            <p className="text-xs text-neutral-600 line-clamp-2">
              {clip.promptSummary || clip.videoPrompt || clip.description || '(no prompt)'}
            </p>

            {clip.lastError && (
              <p className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">
                {clip.lastError}
              </p>
            )}

            {clip.videoPrompt && (
              <button
                onClick={() => {
                  onLoad({
                    modelId: clip.video?.modelId,
                    prompt: clip.videoPrompt || clip.description,
                    duration: clip.duration,
                    aspectRatio: clip.video?.aspectRatio as '16:9' | '9:16' | undefined,
                    hasAudio: clip.video?.hasAudio,
                    imageUrl: meta?.images?.[0],
                    endFrameUrl: meta?.images?.[1],
                    referenceImageUrls: meta?.referenceImages,
                    sourceVideoUrl: meta?.sourceVideoUrl,
                    negativePrompt: meta?.negativePrompt,
                    cfgScale: meta?.cfgScale,
                  })
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                <Upload className="h-3.5 w-3.5" />
                Load
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

function MediaThumb({ url, label, failed }: { url: string; label: string; failed?: boolean }) {
  return (
    <div className="shrink-0 space-y-0.5">
      <img
        src={url}
        className={cn(
          'w-24 h-14 object-cover rounded border',
          failed ? 'border-red-300 opacity-50' : 'border-neutral-200'
        )}
      />
      <p className="text-[10px] text-neutral-400 text-center">{label}</p>
    </div>
  )
}

// ─── Errors Tab ───

function ErrorsTab({ onLoad }: { onLoad: (params: LoadParams) => void }) {
  const result = useQuery(api.sandbox.listErrors, { limit: 15 })
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const [errorFilter, setErrorFilter] = useState<string | null>(null)

  const errorCategories = useMemo(() => {
    if (!result?.errors.length) return []
    const counts = new Map<string, number>()
    for (const err of result.errors) {
      const cat = categorizeError(err.errorMessage || '')
      counts.set(cat, (counts.get(cat) || 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
  }, [result])

  const filteredErrors = useMemo(() => {
    if (!result?.errors.length) return []
    if (!errorFilter) return result.errors
    return result.errors.filter(
      (err) => categorizeError(err.errorMessage || '') === errorFilter
    )
  }, [result, errorFilter])

  const grouped = useMemo(() => {
    if (!filteredErrors.length) return []
    const map = new Map<string, { title: string; status?: string; email?: string; workflowId: string; errors: typeof filteredErrors }>()
    for (const err of filteredErrors) {
      const key = String(err.workflowId ?? 'unknown')
      if (!map.has(key)) {
        map.set(key, {
          title: err.projectTitle ?? 'Unknown Video',
          status: err.projectStatus ?? undefined,
          email: err.userEmail ?? undefined,
          workflowId: key,
          errors: [],
        })
      }
      map.get(key)!.errors.push(err)
    }
    return Array.from(map.values())
  }, [filteredErrors])

  const toggleProject = (wfId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev)
      if (next.has(wfId)) next.delete(wfId)
      else next.add(wfId)
      return next
    })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-500" />
          Failed Video Generations
          {result && <span className="text-xs font-normal text-neutral-400">({result.errors.length} errors in {grouped.length} videos)</span>}
        </CardTitle>
      </CardHeader>
      {errorCategories.length > 1 && (
        <div className="px-6 pb-2 flex gap-2 flex-wrap">
          <button
            onClick={() => setErrorFilter(null)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
              !errorFilter
                ? 'bg-neutral-900 text-white border-neutral-900'
                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
            )}
          >
            All ({result?.errors.length})
          </button>
          {errorCategories.map(([cat, count]) => (
            <button
              key={cat}
              onClick={() => setErrorFilter(errorFilter === cat ? null : cat)}
              className={cn(
                'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors',
                errorFilter === cat
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
              )}
            >
              {cat} ({count})
            </button>
          ))}
        </div>
      )}
      <CardContent>
        {result === undefined ? (
          <div className="flex items-center justify-center py-8 text-neutral-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : grouped.length === 0 ? (
          <p className="text-sm text-neutral-400 text-center py-8">{errorFilter ? 'No errors match this filter' : 'No video errors found'}</p>
        ) : (
          <div className="divide-y divide-neutral-100">
            {grouped.map((group) => {
              const isOpen = expandedProjects.has(group.workflowId)
              return (
                <div key={group.workflowId}>
                  <button
                    onClick={() => toggleProject(group.workflowId)}
                    className="w-full flex items-center gap-2 py-3 hover:bg-neutral-50 transition-colors text-left"
                  >
                    <ChevronRight className={cn('h-4 w-4 text-neutral-400 transition-transform shrink-0', isOpen && 'rotate-90')} />
                    <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                    <span className="text-sm font-medium text-neutral-900 truncate">{group.title}</span>
                    <span className="text-xs text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">{group.errors.length}</span>
                    {group.email && <span className="text-xs text-neutral-400">{group.email}</span>}
                    {group.status && (
                      <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded ml-auto',
                        group.status === 'completed' ? 'bg-green-50 text-green-700' :
                        group.status === 'generating' ? 'bg-amber-50 text-amber-700' :
                        'bg-neutral-100 text-neutral-600'
                      )}>
                        {group.status}
                      </span>
                    )}
                    <a
                      href={`/video/${group.workflowId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-neutral-400 hover:text-neutral-600 shrink-0"
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </button>

                  {isOpen && (
                    <div className="pl-8 pb-3 space-y-3">
                      {group.errors.map((err) => (
                        <div key={err._id} className="space-y-1.5 bg-neutral-50 rounded-lg p-2.5">
                          <div className="flex items-center gap-2 text-xs">
                            {err.clipSceneLabel && <span className="text-neutral-500">{err.clipSceneLabel} ·</span>}
                            <span className="text-neutral-500">{err.clipTitle || `Clip ${(err.clipOrderIndex ?? 0) + 1}`}</span>
                            <span className="font-mono bg-neutral-200 px-1.5 py-0.5 rounded text-neutral-600">{err.modelId}</span>
                            <span className="text-neutral-400">{err.duration}s · {err.aspectRatio}</span>
                            <span className="text-neutral-400 ml-auto">{formatTimeAgo(err.failedAt || err.createdAt)}</span>
                          </div>
                          <p className="text-xs text-red-600 font-mono bg-red-50 rounded px-2 py-1.5">{err.errorMessage}</p>
                          {err.prompt && <p className="text-xs text-neutral-600 line-clamp-2">{err.prompt}</p>}
                          <button
                            onClick={() => {
                              const meta = err.metadata as Record<string, any> | undefined
                              onLoad({
                                modelId: err.modelId,
                                prompt: err.prompt ?? undefined,
                                duration: err.duration,
                                aspectRatio: err.aspectRatio as '16:9' | '9:16' | undefined,
                                hasAudio: err.hasAudio ?? undefined,
                                imageUrl: meta?.images?.[0],
                                endFrameUrl: meta?.images?.[1],
                                referenceImageUrls: meta?.referenceImages,
                                sourceVideoUrl: meta?.sourceVideoUrl,
                                negativePrompt: meta?.negativePrompt,
                                cfgScale: meta?.cfgScale,
                                iterationGroupId: meta?.iterationGroupId,
                                iterationIndex: meta?.iterationIndex,
                              })
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                          >
                            <Upload className="h-3.5 w-3.5" />
                            Load
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Iteration Timeline ───

function IterationTimeline({
  iterationGroupId,
  onNewSession,
}: {
  iterationGroupId: string
  onNewSession: () => void
}) {
  const videos = useQuery(api.sandbox.listByIterationGroup, { iterationGroupId })
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [videos?.length])

  if (!videos || videos.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Iteration Session</CardTitle>
        <Button variant="outline" size="sm" onClick={onNewSession}>
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          New Session
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-0 max-h-[500px] overflow-y-auto pr-1">
          {videos.map((video, i) => {
            const meta = video.metadata as Record<string, any> | undefined
            const prevVideo = i > 0 ? videos[i - 1] : null
            const prevPrompt = prevVideo?.prompt || ''
            const currentPrompt = video.prompt || ''

            return (
              <div key={video._id} className="flex gap-3 pb-4 last:pb-0">
                {/* Version badge + timeline line */}
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-7 h-7 rounded-full bg-neutral-900 text-white text-xs font-bold flex items-center justify-center">
                    v{(meta?.iterationIndex ?? i) + 1}
                  </div>
                  {i < videos.length - 1 && (
                    <div className="w-px flex-1 bg-neutral-200 mt-1" />
                  )}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 space-y-2 pt-0.5">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={video.status} />
                    <span className="text-xs font-mono bg-neutral-100 px-1.5 py-0.5 rounded">
                      {video.modelId}
                    </span>
                    <span className="text-xs text-neutral-400">
                      {video.duration}s
                    </span>
                    <span className="text-xs text-neutral-400 ml-auto">
                      {formatTimeAgo(video.createdAt)}
                    </span>
                  </div>

                  {/* Word diff or full prompt */}
                  {i === 0 ? (
                    <p className="text-sm text-neutral-700 whitespace-pre-wrap line-clamp-4">
                      {currentPrompt}
                    </p>
                  ) : prevPrompt === currentPrompt ? (
                    <p className="text-xs text-neutral-400 italic">No prompt changes</p>
                  ) : (
                    <WordDiff oldText={prevPrompt} newText={currentPrompt} />
                  )}

                  {/* Video result */}
                  {video.status === 'succeeded' && video.videoUrl ? (
                    <video
                      src={video.videoUrl}
                      controls
                      className="max-w-xs rounded-md"
                      preload="none"
                    />
                  ) : video.status === 'failed' && video.errorMessage ? (
                    <p className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">
                      {video.errorMessage}
                    </p>
                  ) : video.status === 'processing' || video.status === 'pending' ? (
                    <div className="flex items-center gap-1.5 text-xs text-blue-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Generating...
                    </div>
                  ) : null}
                </div>
              </div>
            )
          })}
          <div ref={endRef} />
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Word Diff (LCS-based) ───

function WordDiff({ oldText, newText }: { oldText: string; newText: string }) {
  const diff = useMemo(() => computeWordDiff(oldText, newText), [oldText, newText])

  return (
    <p className="text-sm leading-relaxed whitespace-pre-wrap">
      {diff.map((part, i) =>
        part.type === 'equal' ? (
          <span key={i} className="text-neutral-700">{part.value} </span>
        ) : part.type === 'add' ? (
          <span key={i} className="bg-green-100 text-green-800 rounded-sm px-0.5">{part.value} </span>
        ) : (
          <span key={i} className="bg-red-100 text-red-700 line-through rounded-sm px-0.5">{part.value} </span>
        )
      )}
    </p>
  )
}

type DiffPart = { type: 'equal' | 'add' | 'remove'; value: string }

function computeWordDiff(oldText: string, newText: string): DiffPart[] {
  const oldWords = oldText.split(/\s+/).filter(Boolean)
  const newWords = newText.split(/\s+/).filter(Boolean)

  const n = oldWords.length
  const m = newWords.length

  // Build LCS table
  const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      dp[i][j] = oldWords[i - 1] === newWords[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }

  // Backtrack to produce diff
  const result: DiffPart[] = []
  let i = n, j = m
  const stack: DiffPart[] = []

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      stack.push({ type: 'equal', value: oldWords[i - 1] })
      i--; j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      stack.push({ type: 'add', value: newWords[j - 1] })
      j--
    } else {
      stack.push({ type: 'remove', value: oldWords[i - 1] })
      i--
    }
  }

  stack.reverse()

  // Merge consecutive same-type parts
  for (const part of stack) {
    const last = result[result.length - 1]
    if (last && last.type === part.type) {
      last.value += ' ' + part.value
    } else {
      result.push({ ...part })
    }
  }

  return result
}

// ─── Error categorization ───

function categorizeError(message: string): string {
  const trimmed = message.trim()
  if (!trimmed) return 'Unknown'
  return trimmed.length > 80 ? trimmed.slice(0, 80) + '...' : trimmed
}

// ─── Shared helpers ───

function JsonHighlight({ value }: { value: Record<string, unknown> }) {
  const entries = Object.entries(value)
  if (entries.length === 0) {
    return <span className="text-orange-300">{'{}'}</span>
  }

  return (
    <>
      <span className="text-orange-300">{'{'}</span>
      {entries.map(([key, val], i) => (
        <span key={key}>
          {'\n  '}
          <span className="text-purple-400">{`"${key}"`}</span>
          <span className="text-neutral-500">: </span>
          <JsonValue value={val} indent={2} />
          {i < entries.length - 1 && <span className="text-neutral-500">,</span>}
        </span>
      ))}
      {'\n'}
      <span className="text-orange-300">{'}'}</span>
    </>
  )
}

function JsonValue({ value, indent }: { value: unknown; indent: number }) {
  if (typeof value === 'string') {
    return <span className="text-green-400">{`"${value}"`}</span>
  }
  if (typeof value === 'number') {
    return <span className="text-cyan-300">{value}</span>
  }
  if (typeof value === 'boolean') {
    return <span className="text-yellow-300">{String(value)}</span>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-orange-300">{'[]'}</span>
    const pad = ' '.repeat(indent)
    const innerPad = ' '.repeat(indent + 2)
    return (
      <>
        <span className="text-orange-300">{'['}</span>
        {value.map((item, i) => (
          <span key={i}>
            {'\n' + innerPad}
            <JsonValue value={item} indent={indent + 2} />
            {i < value.length - 1 && <span className="text-neutral-500">,</span>}
          </span>
        ))}
        {'\n' + pad}
        <span className="text-orange-300">{']'}</span>
      </>
    )
  }
  return <span className="text-neutral-400">{JSON.stringify(value)}</span>
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'succeeded':
      return <CheckCircle2 className="h-4 w-4 text-green-500" />
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-500" />
    case 'processing':
      return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
    default:
      return <Clock className="h-4 w-4 text-neutral-400" />
  }
}

function ClipStatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
    case 'failed':
      return <XCircle className="h-3.5 w-3.5 text-red-500" />
    case 'generating_video':
    case 'generating_image':
      return <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
    default:
      return <Clock className="h-3.5 w-3.5 text-neutral-400" />
  }
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}
